import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetFlatList,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import { Pressable, useWindowDimensions, View, Text } from "react-native";
import { useSheet } from "../shared/sheet-provider";
import { CheckboxInput } from "../ui/checkbox-input";
import CloseIcon from "@/assets/svgs/close-icon";
import { remapProps } from "react-native-css-interop";
import { SheetIds } from "../utils/sheet-ids";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import { Button } from "../ui/button";
import {
  isBritishCurriculumLabel,
  isIbCurriculumLabel,
} from "../utils/curriculum";

type Option = {
  label: string;
  value: string;
  disabled?: boolean;
};

type Options = Option[];

export type MultiSelectSheetTab = {
  id: string;
  label: string;
  optionValues: string[];
};

remapProps(BottomSheetView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

export type MultiSelectSheetPayload = {
  options: Option[];
  preSelectedOptions: string[];
  title?: string;
  tabs?: MultiSelectSheetTab[];
};

export type MultiSelectSheetResult = {
  selectedOptions: string[];
};

export function buildCurriculumYearGroupTabs({
  yearGroups,
  curriculums,
  selectedCurriculumIds,
}: {
  yearGroups: { value: string; programmeId?: string }[];
  curriculums: { value: string; label: string }[];
  selectedCurriculumIds: string[];
}): MultiSelectSheetTab[] | undefined {
  const britishCurriculum = curriculums.find((curriculum) =>
    isBritishCurriculumLabel(curriculum.label),
  );
  const ibCurriculum = curriculums.find((curriculum) =>
    isIbCurriculumLabel(curriculum.label),
  );

  if (!britishCurriculum || !ibCurriculum) return undefined;

  const hasBritish = selectedCurriculumIds.includes(britishCurriculum.value);
  const hasIb = selectedCurriculumIds.includes(ibCurriculum.value);
  if (!hasBritish || !hasIb) return undefined;

  const britishValues = yearGroups
    .filter((yearGroup) => !yearGroup.programmeId)
    .map((yearGroup) => yearGroup.value);
  const ibValues = yearGroups
    .filter((yearGroup) => !!yearGroup.programmeId)
    .map((yearGroup) => yearGroup.value);

  if (britishValues.length === 0 || ibValues.length === 0) return undefined;

  return [
    {
      id: britishCurriculum.value,
      label: "British",
      optionValues: britishValues,
    },
    {
      id: ibCurriculum.value,
      label: "IB",
      optionValues: ibValues,
    },
  ];
}

function MultiSelectSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss, sheet } = useSheet(SheetIds.MULTI_SELECT);
  const { payload } = sheet ?? {};
  const options: Options = payload?.options || [];
  const tabs = useMemo((): MultiSelectSheetTab[] => {
    const payloadTabs: MultiSelectSheetTab[] = payload?.tabs ?? [];
    return payloadTabs.filter((tab: MultiSelectSheetTab) => tab.optionValues.length > 0);
  }, [payload?.tabs]);
  const showTabs = tabs.length >= 2;
  const preSelectedOptions: string[] = useMemo(
    () => payload?.preSelectedOptions || [],
    [payload]
  );
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const responsiveWidth = useResponsiveWidth();
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;

  useEffect(() => {
    setShouldRender(true);
  }, []);

  useEffect(() => {
    setSelectedOptions(preSelectedOptions);
  }, [preSelectedOptions]);

  useEffect(() => {
    if (!showTabs) {
      setActiveTabId(null);
      return;
    }
    setActiveTabId((current) => {
      if (current && tabs.some((tab: MultiSelectSheetTab) => tab.id === current))
        return current;
      return tabs[0]?.id ?? null;
    });
  }, [showTabs, tabs]);

  const activeTab = useMemo(
    () =>
      tabs.find((tab: MultiSelectSheetTab) => tab.id === activeTabId) ??
      tabs[0],
    [tabs, activeTabId],
  );

  const visibleOptions = useMemo(() => {
    if (!showTabs || !activeTab) return options;
    const optionValueSet = new Set(activeTab.optionValues);
    return options.filter((option) => optionValueSet.has(option.value));
  }, [showTabs, activeTab, options]);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register]
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        dismiss();
      }
    },
    [dismiss]
  );

  const onClose = useCallback(() => {
    close({
      selectedOptions: preSelectedOptions,
    });
  }, [close, preSelectedOptions]);

  const onDone = useCallback(() => {
    close({
      selectedOptions,
    });
  }, [close, selectedOptions]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose]
  );

  const renderItem = useCallback(
    ({ item }: { item: Option }) => {
      const onPress = () => {
        setSelectedOptions((prev) => {
          if (prev.includes(item.value)) {
            return prev.filter((value) => value !== item.value);
          }
          return prev.length === 0 ? [item.value] : [...prev, item.value];
        });
      };

      return (
        <View className="mx-5">
          <CheckboxInput onPress={onPress} disabled={item.disabled}>
            <CheckboxInput.Label>{item.label}</CheckboxInput.Label>
            <CheckboxInput.Icon
              checked={selectedOptions.includes(item.value)}
            />
          </CheckboxInput>
        </View>
      );
    },
    [selectedOptions]
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      containerStyle={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      <BottomSheetFlatList
        data={visibleOptions}
        renderItem={renderItem}
        ItemSeparatorComponent={() => <View className="h-2" />}
        ListHeaderComponent={() => (
          <View className="bg-teachLoop-creamBase rounded-t-[20]">
            <View className="h-6" />
            <View className="mx-5 flex-row">
              <Text className="font-geologica-medium text-black text-xl leading-[1.5] flex-1">
                {payload?.title}
              </Text>
              <Pressable onPress={onClose} className="active:opacity-80">
                <CloseIcon
                  className="size-6"
                  pathClassName="stroke-teachLoop-textPrimary"
                />
              </Pressable>
            </View>
            {showTabs ? (
              <>
                <View className="h-4" />
                <View className="mx-5 flex-row">
                  {tabs.map((tab: MultiSelectSheetTab) => {
                    const isActive = activeTab?.id === tab.id;
                    return (
                      <Pressable
                        key={tab.id}
                        className="justify-center items-center flex-1"
                        onPress={() => setActiveTabId(tab.id)}
                      >
                        <Text
                          className={`text-xl leading-[1.5] font-medium font-geologica-medium py-[11px] ${isActive ? "text-teal-700" : "text-gray-500"}`}
                        >
                          {tab.label}
                        </Text>
                        <View
                          className={`w-full h-1 ${isActive ? "bg-teal-700" : "bg-gray-200"}`}
                        />
                      </Pressable>
                    );
                  })}
                </View>
              </>
            ) : null}
            <View className="h-6" />
          </View>
        )}
        stickyHeaderIndices={[0]}
        ListFooterComponent={() =>
          visibleOptions.length <= 5 ? (
            <View className="m-5">
              <Button
                className="w-full"
                size="md"
                intent="primary"
                onPress={onDone}
              >
                <Button.Text>Done</Button.Text>
              </Button>
            </View>
          ) : (
            <View className="h-2" />
          )
        }
      />
      {visibleOptions.length > 5 && (
        <View className="m-5">
          <Button
            className="w-full"
            size="md"
            intent="primary"
            onPress={onDone}
          >
            <Button.Text>Done</Button.Text>
          </Button>
        </View>
      )}
    </BottomSheet>
  ) : null;
}

export default MultiSelectSheet;
